import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './../../styles/admin/reportes_views.css';
import * as globals from './../../components/GlobalScripts.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);

const Admin_reportes_cuentas = () => {
    const path_api = localStorage.getItem('path_api');
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState({});
    const [metrics, setMetrics] = useState(null);

    // Array de niveles de membresía
    const niveles = ['Hierro', 'Bronce', 'Plata', 'Oro'];

    // Fechas de filtro
    const options = { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' };
    const formattedToday = new Date().toLocaleDateString('en-CA', options);
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const formattedOneMonthAgo = oneMonthAgo.toLocaleDateString('en-CA', options);
    const [searchDateStart, setSearchDateStart] = useState(formattedOneMonthAgo);
    const [searchDateEnd, setSearchDateEnd] = useState(formattedToday);

    useEffect(() => {
        const user_cache = localStorage.getItem('user');
        if (user_cache) {
            let userParsed = JSON.parse(user_cache);
            if (userParsed) {
                setUser(userParsed);
            }
        }
        get_metrics(searchDateStart, searchDateEnd);
    }, [location]);

    const get_metrics = (dateStart, dateEnd) => {
        globals.openViewCharge();
        axios.post(`${path_api}/admin/reportes/cuentas.php`, {
            rute: globals.getRuteBackend(),
            session_id: globals.get_session_id(),
            keylogin: globals.get_keylogin(),
            searchDateStart: dateStart,
            searchDateEnd: dateEnd,
        })
            .then(response => {
                if (response.data.mensaje === 'success') {
                    setMetrics(response.data.metrics);
                } else {
                    globals.createAlert({
                        type: "error",
                        msg_title: "Error",
                        msg: response.data.mensaje || "No se obtuvieron las métricas",
                        effect: "opacidad 2s, desplazamiento 2s",
                        time_life: 4000
                    });
                }
            })
            .catch(error => {
                globals.createAlert({
                    type: "error",
                    msg_title: "Error",
                    msg: "Error al cargar las métricas",
                    effect: "opacidad 2s, desplazamiento 2s",
                    time_life: 4000
                });
            })
            .finally(() => {
                globals.closeViewCharge();
            });
    };

    const handleSearchStartChange = (e) => {
        const value = e.target.value;
        setSearchDateStart(value);
        const date = new Date(value);
        if (date.getFullYear() >= 2025) {
            get_metrics(value, searchDateEnd);
        }
    };

    const handleSearchEndChange = (e) => {
        const value = e.target.value;
        setSearchDateEnd(value);
        const date = new Date(value);
        if (date.getFullYear() >= 2025) {
            get_metrics(searchDateStart, value);
        }
    };

    // Contenedor para gráficas verticales de 300px de alto
    const chartContainerStyle = {
        position: 'relative',
        height: '300px',
    };

    // Para gráficos horizontales (Profesión, Estado y Ciudades)
    const profesionLabels = metrics ? Object.keys(metrics.by_profesion || {}) : [];
    const barCountProfesion = profesionLabels.length;
    const dynamicHeightProfesion = barCountProfesion * 40 + 50;
    const horizontalChartContainerStyleProfesion = {
        position: 'relative',
        height: `${dynamicHeightProfesion}px`,
    };

    const estadoLabels = metrics ? Object.keys(metrics.by_estado || {}) : [];
    const barCountEstado = estadoLabels.length;
    const dynamicHeightEstado = barCountEstado * 40 + 50;
    const horizontalChartContainerStyleEstado = {
        position: 'relative',
        height: `${dynamicHeightEstado}px`,
    };

    const ciudadLabels = metrics ? Object.keys(metrics.by_ciudad || {}) : [];
    const barCountCiudad = ciudadLabels.length;
    const dynamicHeightCiudad = barCountCiudad * 40 + 50;
    const horizontalChartContainerStyleCiudad = {
        position: 'relative',
        height: `${dynamicHeightCiudad}px`,
    };

    // --- Configuración de las gráficas ---

    // Cuentas por Rol (Tipo Pastel)
    const cuentasRolChartData = {
        labels: ['Cliente', 'Profesional'],
        datasets: [
            {
                data: [
                    metrics && metrics.by_rol && metrics.by_rol[1] ? metrics.by_rol[1] : 0,
                    metrics && metrics.by_rol && metrics.by_rol[2] ? metrics.by_rol[2] : 0
                ],
                backgroundColor: ['#36A2EB', '#2ecc71'],
                hoverBackgroundColor: ['#36A2EB', '#2ecc71'],
            },
        ],
    };

    const cuentasRolChartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 4 / 3,
        plugins: {
            legend: {
                position: 'bottom',
            },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                textShadowColor: '#000',
                textShadowBlur: 2,
                formatter: (value) => value,
            },
        },
    };

    // Cuentas por Nivel de Membresía (Tipo Pastel)
    // Mapeamos las claves numéricas al nombre del nivel usando el array "niveles"
    const membershipKeys = metrics ? Object.keys(metrics.by_nivel || {}) : [];
    const membershipLabels = membershipKeys.map(key => niveles[parseInt(key)] || key);
    const membershipData = metrics ? membershipKeys.map(key => metrics.by_nivel[key]) : [];

    const cuentasNivelChartData = {
        labels: membershipLabels,
        datasets: [
            {
                data: membershipData,
                backgroundColor: membershipLabels.map((_, index) => {
                    const hue = Math.floor((360 / membershipLabels.length) * index);
                    return `hsl(${hue}, 70%, 50%)`;
                }),
                hoverBackgroundColor: membershipLabels.map((_, index) => {
                    const hue = Math.floor((360 / membershipLabels.length) * index);
                    return `hsl(${hue}, 70%, 50%)`;
                }),
            },
        ],
    };

    const cuentasNivelChartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 4 / 3,
        plugins: {
            legend: { position: 'bottom' },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                textShadowColor: '#000',
                textShadowBlur: 2,
                formatter: (value) => value,
            },
        },
    };

    // Profesionales por Profesión (Horizontal)
    const profesionColors = profesionLabels.length > 0 ? profesionLabels.map((_, index) => {
        const hue = Math.floor((360 / profesionLabels.length) * index);
        return `hsl(${hue}, 70%, 50%)`;
    }) : [];
    const cuentasProfesionChartData = {
        labels: profesionLabels,
        datasets: [
            {
                label: 'Profesionales',
                data: metrics ? Object.values(metrics.by_profesion || {}) : [],
                backgroundColor: profesionColors,
                hoverBackgroundColor: profesionColors,
            },
        ],
    };

    const cuentasProfesionChartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
              },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                textShadowColor: '#000',
                textShadowBlur: 2,
                formatter: (value) => value,
            },
        },
    };

    // Cuentas por Estado (Horizontal)
    const estadoColors = estadoLabels.length > 0 ? estadoLabels.map((_, index) => {
        const hue = Math.floor((360 / estadoLabels.length) * index);
        return `hsl(${hue}, 70%, 50%)`;
    }) : [];
    const cuentasEstadoChartData = {
        labels: estadoLabels,
        datasets: [
            {
                label: 'Cuentas',
                data: metrics ? Object.values(metrics.by_estado || {}) : [],
                backgroundColor: estadoColors,
                hoverBackgroundColor: estadoColors,
            },
        ],
    };

    const cuentasEstadoChartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
              },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                textShadowColor: '#000',
                textShadowBlur: 2,
                formatter: (value) => value,
            },
        },
    };

    // Ciudades (Horizontal)
    const ciudadColors = ciudadLabels.length > 0 ? ciudadLabels.map((_, index) => {
        const hue = Math.floor((360 / ciudadLabels.length) * index);
        return `hsl(${hue}, 70%, 50%)`;
    }) : [];
    const cuentasCiudadChartData = {
        labels: ciudadLabels,
        datasets: [
            {
                label: 'Cuentas',
                data: metrics ? Object.values(metrics.by_ciudad || {}) : [],
                backgroundColor: ciudadColors,
                hoverBackgroundColor: ciudadColors,
            },
        ],
    };

    const cuentasCiudadChartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
              },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                textShadowColor: '#000',
                textShadowBlur: 2,
                formatter: (value) => value,
            },
        },
    };

    return (
        <div className="page-reportes-views container-section flex-column gap-20">
            <button onClick={() => navigate(-1)} className="flex-row gap-5 button-back">
                <svg width="20" viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24.163 41.125L38.7085 55.6658L38.7088 55.6662C39.2779 56.2353 39.7294 56.9109 40.0374 57.6545C40.3454 58.3981 40.5039 59.1951 40.5039 60C40.5039 60.8049 40.3454 61.6018 40.0374 62.3454C39.7294 63.089 39.2779 63.7647 38.7088 64.3338C38.1397 64.9029 37.464 65.3544 36.7204 65.6624C35.9768 65.9704 35.1798 66.1289 34.375 66.1289C33.5701 66.1289 32.7732 65.9704 32.0296 65.6624C31.2859 65.3544 30.6103 64.9029 30.0412 64.3338L5.04386 39.3365C5.04297 39.3356 5.04207 39.3347 5.04118 39.3338M24.163 41.125L36.5875 12.2125C36.878 11.9219 37.1085 11.577 37.2658 11.1974C37.423 10.8178 37.5039 10.4109 37.5039 10C37.5039 9.5891 37.423 9.18222 37.2658 8.8026C37.1085 8.42298 36.878 8.07805 36.5875 7.7875C36.2969 7.49695 35.952 7.26647 35.5724 7.10923C35.1928 6.95198 34.7859 6.87105 34.375 6.87105C33.9641 6.87105 33.5572 6.95198 33.1776 7.10923C32.798 7.26647 32.453 7.49695 32.1625 7.7875L7.1625 32.7875C6.87148 33.0778 6.64059 33.4226 6.48305 33.8023C6.32551 34.1819 6.24441 34.589 6.24441 35C6.24441 35.411 6.32551 35.818 6.48305 36.1977C6.64059 36.5774 6.87148 36.9222 7.1625 37.2125L5.04118 39.3338"
                        fill="var(--color-primary)" stroke="var(--color-primary)" strokeWidth="6" />
                </svg>
                <h4 style={{ color: "var(--color-primary)" }}>Volver</h4>
            </button>
            <h3>Resumen de cuentas</h3>

            <div className="container-search">
                <div className="search">
                    <label>A partir de la fecha:</label>
                    <input type="date" value={searchDateStart} onChange={handleSearchStartChange} className="input-primary" />
                </div>
                <div className="search">
                    <label>Hasta la fecha:</label>
                    <input type="date" value={searchDateEnd} onChange={handleSearchEndChange} className="input-primary" />
                </div>
            </div>

            {metrics && (
                <div className="chart-grid-container">
                    <div className="chart-item">
                        <h4>Cuentas por rol</h4>
                        <div style={chartContainerStyle}>
                            <Pie data={cuentasRolChartData} options={cuentasRolChartOptions} />
                        </div>
                    </div>

                    <div className="chart-item">
                        <h4>Membresias de profesionales</h4>
                        <div style={chartContainerStyle}>
                            <Pie data={cuentasNivelChartData} options={cuentasNivelChartOptions} />
                        </div>
                    </div>

                    {metrics.by_profesion && Object.keys(metrics.by_profesion).length > 0 && (
                        <div className="chart-item">
                            <h4>Profesiones principales</h4>
                            <div style={horizontalChartContainerStyleProfesion}>
                                <Bar data={cuentasProfesionChartData} options={cuentasProfesionChartOptions} />
                            </div>
                        </div>
                    )}

                    <div className="chart-item">
                        <h4>Estados</h4>
                        <div style={horizontalChartContainerStyleEstado}>
                            <Bar data={cuentasEstadoChartData} options={cuentasEstadoChartOptions} />
                        </div>
                    </div>

                    <div className="chart-item">
                        <h4>Ciudades</h4>
                        <div style={horizontalChartContainerStyleCiudad}>
                            <Bar data={cuentasCiudadChartData} options={cuentasCiudadChartOptions} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin_reportes_cuentas;
